// =============================================================================
// HSE evidence photo upload — T-423b
// =============================================================================
// The generic form photo path was broken from day one: capture.ts's
// uploadPhoto() finishes at /photos/finalise, which inserts into
// acceptance_photos and REQUIRES a HOP pack_item_id — a form photo has none,
// so every incident photo 500'd ("Try again").
//
// HSE evidence goes to its own Supabase Storage bucket instead (hse-evidence,
// mig 243, under git since 482): shrunk on-device, no watermark (2 Nov 2026),
// then a plain storage upload. Offline: blob cached in IndexedDB + an 'hse_photo'
// queue item replays the upload to the SAME pre-minted path, so the paths
// recorded in the form submission stay valid either way.
// =============================================================================

import { supabase } from "@/lib/supabase";
import { cachePhoto, queueItem } from "@/lib/offline";
import type { WatermarkContext } from "@/features/photos/capture";
import type { GpsFix } from "@/features/gps/useGps";

export const HSE_EVIDENCE_BUCKET = "hse-evidence";

export interface HseEvidencePhoto {
  path: string;               // storage path in hse-evidence
  captured_at: string;
  taken_known?: boolean;      // 2 Nov 2026 — true when captured_at came from the photo's own EXIF; false = upload time
  gps_lat?: number;
  gps_lng?: number;
  gps_accuracy_m?: number;
  file_name?: string;
  queued_offline: boolean;
  preview_url?: string;       // object URL for immediate thumbnail (session only)
}

// 2 Nov 2026 — James, off INC-26-0004: "HSE incident photos, cut the
// watermark off; if photos are loaded from a library it looks shit and uses
// the load date, which will provide fake details. If it can't be right, have
// nothing." So an incident photo is shrunk and sent as it is — no stamp. Its
// time and place come from the photo's own EXIF when it has them (taken_known
// = true); otherwise captured_at is the upload time and taken_known is false,
// and the reports say "uploaded", never "taken". The phone's GPS fix is only
// used when the file carries no date at all (a fresh camera shot).
const HSE_MAX_LONG_EDGE = 2048, HSE_JPEG_QUALITY = 0.88;

async function shrinkForUpload(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error("image_load_failed")); i.src = url; });
    const long = Math.max(img.width, img.height); const k = long > HSE_MAX_LONG_EDGE ? HSE_MAX_LONG_EDGE / long : 1;
    const cv = document.createElement("canvas"); cv.width = Math.round(img.width * k); cv.height = Math.round(img.height * k);
    cv.getContext("2d")!.drawImage(img, 0, 0, cv.width, cv.height);
    return await new Promise<Blob>((res, rej) => cv.toBlob(b => b ? res(b) : rej(new Error("blob_failed")), "image/jpeg", HSE_JPEG_QUALITY));
  } finally { URL.revokeObjectURL(url); }
}

async function readExif(file: File): Promise<{ takenAt: string | null; lat: number | null; lng: number | null }> {
  try {
    const exifr = (await import("exifr")).default ?? (await import("exifr"));
    const r = await (exifr as any).parse(file, { pick: ["DateTimeOriginal", "GPSLatitude", "GPSLongitude", "GPSLatitudeRef", "GPSLongitudeRef"], gps: true });
    const takenAt = r?.DateTimeOriginal instanceof Date && !isNaN(r.DateTimeOriginal.getTime()) ? r.DateTimeOriginal.toISOString() : null;
    const lat = typeof r?.latitude === "number" && isFinite(r.latitude) ? r.latitude : null;
    const lng = typeof r?.longitude === "number" && isFinite(r.longitude) ? r.longitude : null;
    return { takenAt, lat, lng };
  } catch { return { takenAt: null, lat: null, lng: null }; }
}

export async function uploadHseEvidence(
  file: File,
  _ctx: WatermarkContext,     // kept for the callers; no stamp is drawn any more (2 Nov 2026)
  gps: GpsFix | null,
  keyPrefix: string,          // e.g. `incidents/${submissionId}`
): Promise<HseEvidencePhoto> {
  const [blob, exif] = await Promise.all([shrinkForUpload(file), readExif(file)]);
  const localId = crypto.randomUUID();
  const path = `${keyPrefix}/${localId}.jpg`;
  const fresh = !exif.takenAt;   // no date in the file: treat as taken now, on this phone
  const base: HseEvidencePhoto = {
    path,
    captured_at: exif.takenAt ?? new Date().toISOString(),
    taken_known: !!exif.takenAt,
    gps_lat: exif.lat ?? (fresh ? gps?.latitude : undefined),
    gps_lng: exif.lng ?? (fresh ? gps?.longitude : undefined),
    gps_accuracy_m: exif.lat != null ? undefined : (fresh ? gps?.accuracy_m : undefined),
    file_name: file.name,
    queued_offline: false,
    preview_url: URL.createObjectURL(blob),
  };

  if (!navigator.onLine) {
    await cachePhoto(`hse_${localId}`, blob, { kind: "hse_evidence", path });
    await queueItem("hse_photo", { cache_key: `hse_${localId}`, path });
    return { ...base, queued_offline: true };
  }

  const { error } = await supabase.storage
    .from(HSE_EVIDENCE_BUCKET)
    .upload(path, blob, { contentType: "image/jpeg", upsert: true });
  if (error) {
    // Network wobble mid-flight — fall back to the offline queue rather than
    // losing the photo (the T-401 principle: evidence is never discarded).
    await cachePhoto(`hse_${localId}`, blob, { kind: "hse_evidence", path });
    await queueItem("hse_photo", { cache_key: `hse_${localId}`, path });
    return { ...base, queued_offline: true };
  }

  return base;
}

// 1 Nov 2026 — drawings (the accident map, the damage diagrams) go up as they
// are: a PNG, no watermark (James: "we do not need to watermark the
// drawings"). Same bucket, same key prefix, same offline fallback as a photo.
export async function uploadHseDrawing(blob: Blob, keyPrefix: string, name: string): Promise<HseEvidencePhoto> {
  const localId = crypto.randomUUID();
  const path = `${keyPrefix}/${name.replace(/[^\w.-]+/g, "_").replace(/\.png$/i, "")}_${localId.slice(0, 8)}.png`;
  const base: HseEvidencePhoto = { path, captured_at: new Date().toISOString(), file_name: name, queued_offline: false };
  if (!navigator.onLine) {
    await cachePhoto(`hse_${localId}`, blob, { kind: "hse_evidence", path });
    await queueItem("hse_photo", { cache_key: `hse_${localId}`, path });
    return { ...base, queued_offline: true };
  }
  const { error } = await supabase.storage.from(HSE_EVIDENCE_BUCKET).upload(path, blob, { contentType: "image/png", upsert: true });
  if (error) {
    await cachePhoto(`hse_${localId}`, blob, { kind: "hse_evidence", path });
    await queueItem("hse_photo", { cache_key: `hse_${localId}`, path });
    return { ...base, queued_offline: true };
  }
  return base;
}

export async function signedEvidenceUrl(path: string, expiresIn = 3600): Promise<string | null> {
  const { data } = await supabase.storage.from(HSE_EVIDENCE_BUCKET).createSignedUrl(path, expiresIn);
  return data?.signedUrl ?? null;
}
